import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
  FC,
} from "react";
import CustomSnackbar from "../Components/Snackbar/Snackbar";

type SnackbarSeverity = "success" | "error" | "info" | "warning";

interface SnackbarData {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
  title?: string;
}

interface SnackbarContextType {
  showSnackbar: (
    message: string,
    severity?: SnackbarSeverity,
    title?: string
  ) => void;
}

interface SnackbarProviderProps {
  children: ReactNode;
}

const SnackbarContext = createContext<SnackbarContextType | null>(null);

export const SnackbarProvider: FC<SnackbarProviderProps> = ({
  children,
}: SnackbarProviderProps)  => {
  const [snackbar, setSnackbar] = useState<SnackbarData>({
    open: false,
    message: "",
    severity: "info",
  });

  const showSnackbar = useCallback(
  (message: string, severity: SnackbarSeverity = "info", title?: string) => {
    setSnackbar({ open: true, message, severity, title });
  },
  []
);


  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <CustomSnackbar
        open={snackbar.open}
        onClose={closeSnackbar}
        snackbarData={{
          message: snackbar.message,
          severity: snackbar.severity,
          title: snackbar.title,
        }}
      />
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};
